/**
 * Биты прав. Повторяют `Permission.cs` в airel-cs, включая пропуски:
 * промежутки там оставлены под будущее, и сдвигать номера нельзя — они
 * лежат в базе числами.
 *
 * Значения — `bigint`, и это не педантизм. Побитовые операторы JavaScript
 * приводят операнды к **32-битному знаковому** числу, а верхний бит здесь —
 * сороковой. Через `&` половина прав — голос, упоминания, журнал — считалась
 * бы неверно, причём молча: `1 << 31` там даже не переполняется в ноль, а
 * становится отрицательным.
 *
 * Вся маска умещается в 52 бита, поэтому по проводу она ходит обычным числом
 * без потерь; в `bigint` её переводят на входе и обратно на выходе.
 */
export const Permission = {
  ManageChannel: 1n << 0n,
  ManageServer: 1n << 1n,
  ManagePermissions: 1n << 2n,
  ManageRole: 1n << 3n,
  /** «Управлять выражениями»: любые знаки, стикеры и звуки сервера. */
  ManageCustomisation: 1n << 4n,
  /** «Создавать средства выражения»: заводить их и править свои. */
  CreateExpressions: 1n << 5n,

  KickMembers: 1n << 6n,
  BanMembers: 1n << 7n,
  TimeoutMembers: 1n << 8n,
  AssignRoles: 1n << 9n,
  ChangeNickname: 1n << 10n,
  ManageNicknames: 1n << 11n,
  ChangeAvatar: 1n << 12n,
  RemoveAvatars: 1n << 13n,
  /** Все права мимо переопределений каналов — как ADMINISTRATOR у Discord. */
  Administrator: 1n << 14n,
  SetVoiceChannelStatus: 1n << 15n,

  ViewChannel: 1n << 20n,
  ReadMessageHistory: 1n << 21n,
  SendMessage: 1n << 22n,
  ManageMessages: 1n << 23n,
  ManageWebhooks: 1n << 24n,
  InviteOthers: 1n << 25n,
  SendEmbeds: 1n << 26n,
  UploadFiles: 1n << 27n,
  Masquerade: 1n << 28n,
  React: 1n << 29n,

  Connect: 1n << 30n,
  Speak: 1n << 31n,
  Video: 1n << 32n,
  MuteMembers: 1n << 33n,
  DeafenMembers: 1n << 34n,
  MoveMembers: 1n << 35n,
  Listen: 1n << 36n,

  MentionEveryone: 1n << 37n,
  MentionRoles: 1n << 38n,
  BypassSlowmode: 1n << 39n,
  ViewAuditLogs: 1n << 40n,
  ManageEvents: 1n << 41n,
  UseSoundboard: 1n << 42n,
  UseExternalSounds: 1n << 43n,
  /** Команды ботов в «/» — USE_APPLICATION_COMMANDS у Discord. */
  UseApplicationCommands: 1n << 44n,

  // Выделены из крупных прав 3 ноября, как у Discord (миграция 20261103120000 в airel-cs).
  SendMessagesInThreads: 1n << 45n,
  CreatePublicThreads: 1n << 46n,
  ManageThreads: 1n << 47n,
  PinMessages: 1n << 48n,
  SendVoiceMessages: 1n << 49n,
  SendPolls: 1n << 50n,
  CreateEvents: 1n << 51n,
} as const;

export type PermissionName = keyof typeof Permission;

/**
 * Строка в списке прав роли.
 *
 * Без `key` — право Discord, возможности под которое у нас ещё нет: строка
 * видна с пометкой «план», чтобы список совпадал с Discord, но включить её
 * нечем. С `key` и `planned` — бит уже заведён, а проверяющего кода нет.
 */
export interface PermissionItem {
  key?: PermissionName;
  label: string;
  /** Что право даёт — подписью под строкой, как в Discord. */
  note: string;
  planned?: boolean;
  /** Только на сервере: в правах канала строки нет — на канале оно ничего не меняет. */
  serverOnly?: boolean;
  /** Опасное право: подпись предупреждением. */
  danger?: boolean;
}

/**
 * Права роли — разделы и порядок страницы «Права доступа» Discord. Свои права,
 * которых у Discord нет (права каналов, выдача ролей, «Слушать»), стоят рядом с
 * ближайшими по смыслу.
 */
export const PERMISSION_GROUPS: { title: string; items: PermissionItem[] }[] = [
  {
    title: "Основные права сервера",
    items: [
      {
        key: "ViewChannel",
        label: "Просматривать каналы",
        note: "Позволяет участникам по умолчанию видеть каналы, кроме приватных.",
      },
      {
        key: "ManageChannel",
        label: "Управлять каналами",
        note: "Позволяет создавать, редактировать и удалять каналы.",
      },
      {
        key: "ManageRole",
        label: "Управлять ролями",
        note: "Позволяет создавать новые роли, а также редактировать и удалять роли ниже своей самой высокой роли.",
        serverOnly: true,
      },
      {
        key: "ManagePermissions",
        label: "Управлять правами каналов",
        note: "Позволяет менять права ролей и участников в каналах и категориях, к которым есть доступ. У Discord это часть права «Управлять ролями».",
      },
      {
        key: "AssignRoles",
        label: "Выдавать роли",
        note: "Позволяет выдавать и снимать участникам роли ниже своей самой высокой роли. У Discord это часть права «Управлять ролями».",
        serverOnly: true,
      },
      {
        key: "CreateExpressions",
        label: "Создавать средства выражения эмоций",
        note: "Позволяет добавлять на сервер свои эмодзи, стикеры и звуки, а также редактировать и удалять добавленные собой.",
        serverOnly: true,
      },
      {
        key: "ManageCustomisation",
        label: "Управлять выражениями",
        note: "Позволяет редактировать и удалять любые эмодзи, стикеры и звуки сервера.",
        serverOnly: true,
      },
      {
        key: "ViewAuditLogs",
        label: "Просматривать журнал аудита",
        note: "Позволяет смотреть историю изменений сервера.",
        serverOnly: true,
      },
      {
        key: "ManageWebhooks",
        label: "Управлять вебхуками",
        note: "Позволяет создавать, редактировать и удалять вебхуки, через которые на сервер приходят сообщения из других приложений и с сайтов.",
      },
      {
        key: "ManageServer",
        label: "Управлять сервером",
        note: "Позволяет переименовывать сервер, смотреть все приглашения, добавлять приложения, а также создавать и обновлять правила Автомода.",
        serverOnly: true,
      },
    ],
  },
  {
    title: "Права участников",
    items: [
      {
        key: "InviteOthers",
        label: "Создание приглашения",
        note: "Позволяет приглашать на сервер новых участников.",
      },
      {
        key: "ChangeNickname",
        label: "Изменить никнейм",
        note: "Позволяет менять свой никнейм, который действует только на этом сервере.",
        serverOnly: true,
      },
      {
        key: "ManageNicknames",
        label: "Управлять никнеймами",
        note: "Позволяет менять никнеймы других участников.",
        serverOnly: true,
      },
      {
        key: "KickMembers",
        label: "Выгонять участников",
        note: "Позволяет удалять участников с сервера. Вернуться они смогут только по новому приглашению.",
        serverOnly: true,
      },
      {
        key: "BanMembers",
        label: "Банить участников",
        note: "Позволяет навсегда банить участников и удалять их историю сообщений.",
        serverOnly: true,
      },
      {
        key: "TimeoutMembers",
        label: "Отправить участников подумать о своём поведении",
        note: "Тайм-аут: такие участники не могут писать в чат, отвечать в ветках, ставить реакции и говорить в голосовом чате или на трибуне.",
        serverOnly: true,
      },
      {
        key: "ChangeAvatar",
        label: "Менять аватар на сервере",
        note: "Позволяет ставить себе аватар, который виден только на этом сервере.",
        planned: true,
        serverOnly: true,
      },
      {
        key: "RemoveAvatars",
        label: "Снимать аватары участников",
        note: "Позволяет снимать аватары, которые участники поставили себе на этом сервере.",
        planned: true,
        serverOnly: true,
      },
    ],
  },
  {
    title: "Права текстового канала",
    items: [
      {
        key: "SendMessage",
        label: "Отправка сообщений и создание публикаций",
        note: "Позволяет писать в текстовых каналах и создавать публикации на форумах.",
      },
      {
        key: "SendMessagesInThreads",
        label: "Отправлять сообщения в ветках и публикациях",
        note: "Позволяет писать в ветках и в публикациях форумов.",
      },
      {
        key: "CreatePublicThreads",
        label: "Создать публичные ветки",
        note: "Позволяет создавать ветки, которые видят все участники канала.",
      },
      {
        label: "Создание приватных веток",
        note: "Позволяет создавать ветки, в которые можно попасть только по приглашению.",
        planned: true,
      },
      {
        key: "SendEmbeds",
        label: "Встраивать ссылки",
        note: "Показывает в текстовых каналах содержимое ссылок, которыми делятся участники.",
      },
      {
        key: "UploadFiles",
        label: "Прикреплять файлы",
        note: "Позволяет загружать в текстовые каналы файлы и медиа.",
      },
      {
        key: "React",
        label: "Добавлять реакции",
        note: "Позволяет ставить на сообщения новые реакции. Без этого права можно только присоединяться к уже поставленным.",
      },
      {
        label: "Использовать внешние эмодзи",
        note: "Позволяет использовать эмодзи с других серверов.",
        planned: true,
      },
      {
        label: "Использовать внешние стикеры",
        note: "Позволяет использовать стикеры с других серверов.",
        planned: true,
      },
      {
        key: "MentionEveryone",
        label: "Упоминание @everyone и @here",
        note: "Позволяет упоминать @everyone (все участники сервера) и @here (участники канала, которые в сети).",
      },
      {
        key: "MentionRoles",
        label: "Упоминание всех ролей",
        note: "Позволяет упоминать любые роли, включая те, у которых отключено «Разрешить всем упоминать эту роль».",
      },
      {
        key: "ManageMessages",
        label: "Управлять сообщениями",
        note: "Позволяет удалять чужие сообщения и вложения в них.",
      },
      {
        key: "PinMessages",
        label: "Закреплять сообщения",
        note: "Позволяет закреплять и откреплять любые сообщения.",
      },
      {
        key: "BypassSlowmode",
        label: "Обход медленного режима",
        note: "Позволяет писать без ограничений медленного режима.",
      },
      {
        key: "ManageThreads",
        label: "Управление ветками и публикациями",
        note: "Позволяет переименовывать, удалять, закрывать и запирать ветки и публикации, а также включать в них медленный режим.",
      },
      {
        key: "ReadMessageHistory",
        label: "Читать историю сообщений",
        note: "Позволяет читать сообщения, отправленные раньше. Без этого права видны только сообщения, пришедшие, пока участник был в сети.",
      },
      {
        label: "Отправка сообщений text-to-speech",
        note: "Позволяет отправлять TTS-сообщения через /tts. Их слышат все в канале.",
        planned: true,
      },
      {
        key: "SendVoiceMessages",
        label: "Отправлять голосовые сообщения",
        note: "Позволяет отправлять голосовые сообщения.",
      },
      {
        key: "SendPolls",
        label: "Создание опросов",
        note: "Позволяет создавать опросы.",
      },
      {
        key: "Masquerade",
        label: "Подменять имя в сообщениях",
        note: "Позволяет писать от другого имени и с другим аватаром, как вебхук.",
        planned: true,
      },
    ],
  },
  {
    title: "Права голосового канала",
    items: [
      {
        key: "Connect",
        label: "Подключаться",
        note: "Позволяет заходить в голосовые каналы.",
      },
      {
        key: "Listen",
        label: "Слушать",
        note: "Позволяет слышать других в голосовых каналах. У Discord это часть права «Подключаться».",
      },
      {
        key: "Speak",
        label: "Говорить",
        note: "Позволяет говорить в голосовых каналах. Без этого права участник молчит, пока кто-то с правом «Отключать участникам микрофон» не включит ему микрофон.",
      },
      {
        key: "Video",
        label: "Видео",
        note: "Позволяет показывать видео и экран.",
      },
      {
        key: "UseSoundboard",
        label: "Использовать звуковую панель",
        note: "Позволяет проигрывать звуки со звуковой панели сервера.",
      },
      {
        key: "UseExternalSounds",
        label: "Использовать внешние звуки",
        note: "Позволяет проигрывать звуки с других своих серверов.",
      },
      {
        label: "Использовать режим активации по голосу",
        note: "Микрофон включается, как только участник начинает говорить. Без этого права нужен режим рации.",
        planned: true,
      },
      {
        label: "Приоритетный режим",
        note: "Участника лучше слышно: громкость остальных, у кого нет этого права, автоматически понижается.",
        planned: true,
      },
      {
        key: "MuteMembers",
        label: "Отключать участникам микрофон",
        note: "Позволяет выключать микрофон другим участникам в голосовых каналах.",
      },
      {
        key: "DeafenMembers",
        label: "Отключать участникам звук",
        note: "Позволяет выключать другим участникам звук, так что они не могут ни говорить, ни слышать.",
      },
      {
        key: "MoveMembers",
        label: "Перемещать участников",
        note: "Позволяет отключать других участников от голосовых каналов или переносить их между каналами, к которым есть доступ.",
      },
      {
        key: "SetVoiceChannelStatus",
        label: "Задать статус голосового канала",
        note: "Позволяет создавать и редактировать статус голосового канала.",
      },
    ],
  },
  {
    title: "Права приложений",
    items: [
      {
        key: "UseApplicationCommands",
        label: "Использовать команды приложения",
        note: "Позволяет пользоваться командами приложений: слэш-командами и командами контекстного меню.",
      },
      {
        label: "Использовать активности",
        note: "Позволяет пользоваться активностями.",
        planned: true,
      },
      {
        label: "Использовать внешние приложения",
        note: "Разрешает приложениям, добавленным к учётной записи участника, публиковать сообщения.",
        planned: true,
      },
    ],
  },
  {
    title: "Права доступа к событиям",
    items: [
      {
        key: "CreateEvents",
        label: "Создавать события",
        note: "Позволяет создавать события, а также редактировать и отменять свои.",
      },
      {
        key: "ManageEvents",
        label: "Управление событиями",
        note: "Позволяет редактировать и отменять любые события.",
      },
    ],
  },
  {
    title: "Расширенные права",
    items: [
      {
        key: "Administrator",
        label: "Администратор",
        note: "Даёт все права и обходит особые права и ограничения каналов, в том числе открывает доступ ко всем приватным каналам. Выдавать это право опасно.",
        serverOnly: true,
        danger: true,
      },
    ],
  },
];

/** По проводу переопределение ходит парой обычных чисел. */
export interface Override {
  allow: number;
  deny: number;
}

export type TriState = "deny" | "unset" | "allow";

/**
 * Где правится право: сама роль или её переопределение на канале.
 *
 * Различать приходится потому, что положений у рубильника два, а состояний
 * в хранилище три. Что писать выключенным — зависит от области, и это не
 * прихоть вида, а разное значение слова «нет».
 */
export type Scope = "role" | "channel";

/**
 * Что записывает выключенное положение.
 *
 * У самой роли — «не задано»: роль права выдаёт, а не отбирает. Запиши она
 * запрет, и открыть редактор с сохранением значило бы отобрать всё, что
 * выдали умолчания сервера и роли ниже.
 *
 * На канале — «запрещено»: переопределение затем и существует, чтобы снять
 * то, что дано выше. Без запрета канал нечем было бы спрятать.
 */
export function offState(scope: Scope): TriState {
  return scope === "channel" ? "deny" : "unset";
}

export function has(permissions: number, bit: bigint): boolean {
  return (BigInt(permissions) & bit) === bit;
}

export function stateOf(override: Override, bit: bigint): TriState {
  if ((BigInt(override.deny) & bit) === bit) return "deny";
  if ((BigInt(override.allow) & bit) === bit) return "allow";
  return "unset";
}

/**
 * Переключение положения. Запрет и разрешение живут в разных числах, поэтому
 * при переходе бит снимается с одного и ставится на другое — иначе право
 * оказалось бы одновременно разрешено и запрещено.
 */
export function withState(override: Override, bit: bigint, state: TriState): Override {
  const allow = BigInt(override.allow);
  const deny = BigInt(override.deny);

  return {
    allow: Number(state === "allow" ? allow | bit : allow & ~bit),
    deny: Number(state === "deny" ? deny | bit : deny & ~bit),
  };
}

/**
 * Итог наложения слоёв — тот же порядок, что в бэкенде: умолчания сервера →
 * переопределение канала для всех → роли сервера → роли канала.
 */
export function apply(base: number, override: Override): number {
  return Number((BigInt(base) & ~BigInt(override.deny)) | BigInt(override.allow));
}

/**
 * Роли сверху вниз: старшая первой.
 *
 * Порядок один на три места — редактор ролей, состав сервера и создание
 * канала, — и разойтись им нельзя: в одном списке роль стоит выше другой
 * ровно тогда, когда она сильнее и в правах.
 *
 * Новый массив, а не сортировка на месте: пришедший с сервера сюда приезжает
 * из кэша запросов, и переставлять в нём — переставлять у всех, кто на него
 * смотрит.
 */
export function byRank<T extends { rank: number }>(roles: readonly T[]): T[] {
  return [...roles].sort((a, b) => b.rank - a.rank);
}
